import React, { FC, useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import dayjs from "dayjs";

import { QUERY_KEYS } from "consts";
import { updateIntensity, updateIntensitysite } from "api";
import { useGetAllowedValues, useNotify } from "hooks";
import { extractErrorMessage, getTextFieldColorStateProps } from "utils";
import { InputFieldWrapper } from "components";
import { RecDatePickerFormField } from "components/inputs/rec-date-picker-form-field";

import { AddIntensityModalProps } from "./types";

export const AddIntensityModal: FC<AddIntensityModalProps> = ({
  isOpen,
  onClose,
  onCancel,
  Intensity,
  editSite,
}) => {
  const notify = useNotify();
  const queryClient = useQueryClient();

  const [intensityCategory, setIntensityCategory] = useState<string>("");
  const [unitOptions, setUnitOptions] = useState<string[] | undefined>([]);

  const { mutateAsync: updateIntensityRequest } = useMutation(
    Intensity === "Company Level" ? updateIntensity : updateIntensitysite,
  );
  const { data: allowedValues } = useGetAllowedValues();

  const defaultValues = useMemo(
    () => ({
      id: editSite?.original?.id,
      startDate: dayjs(editSite?.original?.startDate, "YYYY-MM-DD"),
      endDate: dayjs(editSite?.original?.endDate, "YYYY-MM-DD"),
      intensityCategory,
      value: editSite?.original?.value,
      unit: editSite?.original?.unit,
      siteName: editSite?.original?.siteName,
    }),
    [editSite, intensityCategory],
  );

  const { control, formState, reset, getValues } = useForm<any>({
    defaultValues,
    mode: "onChange",
  });

  const handleEditIntensity = async () => {
    const formattedEndDate = dayjs(getValues("endDate")).format("YYYY-MM-DD");
    const formattedStartDate = dayjs(getValues("startDate")).format(
      "YYYY-MM-DD",
    );

    let updatedIntensity: any;
    if (Intensity === "Company Level") {
      updatedIntensity = {
        id: getValues("id"),
        endDate: formattedEndDate,
        startDate: formattedStartDate,
        intensityCategory: getValues("intensityCategory"),
        unit: getValues("unit"),
        value: getValues("value"),
      };
    } else {
      updatedIntensity = {
        id: getValues("id"),
        endDate: formattedEndDate,
        startDate: formattedStartDate,
        intensityCategory: getValues("intensityCategory"),
        unit: getValues("unit"),
        value: getValues("value"),
      };
    }
    await updateIntensityRequest(updatedIntensity, {
      onError: (error) =>
        notify.error(extractErrorMessage(error, "Failed to edit !")),
      onSuccess: () => {
        notify.success("Intensity data edited successfully.");
        queryClient.invalidateQueries(
          Intensity === "Company Level"
            ? [QUERY_KEYS.INTENSITY]
            : [QUERY_KEYS.INTENSITYSITE],
        );
      },
    });
    onClose();
  };

  useEffect(() => {
    if (editSite?.original !== undefined) {
      setIntensityCategory(editSite?.original?.intensityCategory);
    }
  }, [editSite]);

  useEffect(() => {
    if (intensityCategory === "AREA") {
      setUnitOptions(allowedValues?.intensityAreaUnits);
    }
    if (intensityCategory === "REVENUE") {
      setUnitOptions(allowedValues?.currencies);
    }
  }, [intensityCategory, allowedValues]);

  useEffect(() => {
    reset(defaultValues);
  }, [reset, defaultValues, editSite]);

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle>
        {Intensity === "Company Level"
          ? "Edit Company Level"
          : "Edit Site Level"}
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            columnGap: 3,
            rowGap: 2,
          }}
        >
          {Intensity !== "Company Level" && (
            <InputFieldWrapper label="Site Name">
              <Controller
                name="siteName"
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <TextField
                    helperText={error ? error.message : null}
                    size="small"
                    error={!!error}
                    onChange={onChange}
                    disabled
                    value={value}
                    fullWidth
                    variant="outlined"
                  />
                )}
              />
            </InputFieldWrapper>
          )}
          <RecDatePickerFormField
            name="startDate"
            label="Start Date"
            control={control}
            disabled={false}
            rules={{
              required: editSite?.original?.startDate,
            }}
            {...getTextFieldColorStateProps("startDate", formState.errors)}
          />

          <RecDatePickerFormField
            name="endDate"
            label="End Date"
            control={control}
            disabled={false}
            rules={{
              required: editSite?.original?.endDate,
            }}
            {...getTextFieldColorStateProps("startDate", formState.errors)}
          />
          <InputFieldWrapper label="Intensity Category">
            <Controller
              name="intensityCategory"
              control={control}
              render={({ field }) => (
                <Select
                  labelId="intensity-category-simple-select-label"
                  id="intensity-category-simple-select"
                  autoWidth
                  fullWidth
                  sx={{ height: 40 }}
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    setIntensityCategory(e.target.value);
                  }}
                >
                  {allowedValues?.intensityCategories.map(
                    (category: string) => (
                      <MenuItem value={category}>{category}</MenuItem>
                    ),
                  )}
                </Select>
              )}
            />
          </InputFieldWrapper>
          <InputFieldWrapper label="Value">
            <Controller
              name="value"
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <TextField
                  helperText={error ? error.message : null}
                  size="small"
                  error={!!error}
                  onChange={onChange}
                  value={value}
                  fullWidth
                  variant="outlined"
                />
              )}
            />
          </InputFieldWrapper>
          {intensityCategory !== "PRODUCT_UNIT" ? (
            <InputFieldWrapper label="Unit">
              <Controller
                name="unit"
                control={control}
                render={({ field }) => (
                  <Select
                    labelId="unit-category-simple-select-label"
                    id="unit-category-simple-select"
                    autoWidth
                    fullWidth
                    sx={{ height: 40 }}
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                  >
                    {unitOptions?.map((unitOption: string) => (
                      <MenuItem value={unitOption}>{unitOption}</MenuItem>
                    ))}
                  </Select>
                )}
              />
            </InputFieldWrapper>
          ) : (
            <InputFieldWrapper label="Unit">
              <Controller
                name="unit"
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <TextField
                    helperText={error ? error.message : null}
                    size="small"
                    error={!!error}
                    onChange={onChange}
                    value={value}
                    fullWidth
                    variant="outlined"
                  />
                )}
              />
            </InputFieldWrapper>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          onClick={() => handleEditIntensity()}
          variant="contained"
          color="primary"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
